import { useEffect, useMemo, useState } from 'react';
import { BookOpen, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { klassenbuchApi, type KlassenbuchEntry } from '../../lib/klassenbuchApi';

function mondayOf(d: Date) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day + 6) % 7;
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function toDateStr(d: Date) {
  return d.toISOString().slice(0, 10);
}

/**
 * Schüleransicht „Meine Stunden der Woche“.
 * Zeigt Thema, Hausaufgabe-Status und Stundenstatus der aktiven Memberships.
 */
export default function StudentKlassenbuch() {
  const [weekStart, setWeekStart] = useState<Date>(mondayOf(new Date()));
  const [entries, setEntries] = useState<KlassenbuchEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    klassenbuchApi
      .studentWeek(toDateStr(weekStart), toDateStr(addDays(weekStart, 6)))
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [weekStart]);

  const days = useMemo(() => [0, 1, 2, 3, 4].map((i) => addDays(weekStart, i)), [weekStart]);

  const byDay = useMemo(() => {
    const map: Record<string, KlassenbuchEntry[]> = {};
    for (const e of entries) {
      const key = e.start_at ? toDateStr(new Date(e.start_at)) : 'unscheduled';
      if (!map[key]) map[key] = [];
      map[key].push(e);
    }
    return map;
  }, [entries]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900">Mein Klassenbuch</h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="px-3 py-1.5 border border-gray-300 rounded-md bg-white text-sm hover:bg-gray-50 flex items-center gap-1"
          >
            <CalendarDays size={14} /> Heute
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Lade…</div>}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {days.map((d) => {
          const key = toDateStr(d);
          const list = byDay[key] || [];
          const isToday = key === toDateStr(new Date());
          return (
            <div
              key={key}
              className={`border rounded-lg p-2 min-h-[160px] ${
                isToday ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="text-xs font-medium text-gray-600 mb-2">
                {d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
              </div>
              {list.length === 0 && <div className="text-xs text-gray-400">Keine Stunden</div>}
              <div className="space-y-1">
                {list.map((e) => (
                  <div
                    key={e.id}
                    className="border border-gray-200 rounded-md px-2 py-1 text-xs bg-white"
                  >
                    <div className="font-medium text-gray-900">{e.title}</div>
                    {e.topic_summary && (
                      <div className="text-[11px] text-gray-600">{e.topic_summary}</div>
                    )}
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
                      {e.start_at && (
                        <span>
                          {new Date(e.start_at).toLocaleTimeString('de-DE', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                      {e.has_homework ? (
                        <span className="text-blue-600">Hausaufgabe vorhanden</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
